<!-- src/views/OnBoarding/LiteracyTestFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <FormHeader />
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto">
        
        <!-- Premium Header Card -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl">
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight">Literacy & Phonetic Test</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">
                Communication Competency Assessment • Sentinel Safe Premium
              </p>
            </div>
            <div class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3">
              <div class="w-2 h-2 bg-[#20FCF1] rounded-full"></div>
              <span class="text-white text-[10px] font-black uppercase tracking-widest">Assessment Mode</span>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 sm:p-10 rounded-b-[40px] shadow-xl border-x border-b border-slate-200">
          
          <form @submit.prevent="submitForm" class="space-y-16">
            
            <!-- Section 1: Personnel Verification -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <IdentificationIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Your Details</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Full Name</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight">{{ userProfile.name || 'Loading...' }}</p>
                </div>
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Email Address</label>
                  <p class="text-lg font-black text-slate-800 lowercase tracking-tight">{{ userProfile.email || 'Loading...' }}</p>
                </div>
                <div class="bg-white p-6 rounded-2xl border border-[#20FCF1]/30 shadow-sm">
                  <label class="form-label text-[#20FCF1]">Sentinel Number</label>
                  <p class="text-lg font-black text-[#0A1630] tracking-widest uppercase">{{ userProfile.sentinelNumber || 'N/A' }}</p>
                </div>
              </div>
            </section>
            
            <!-- Section 2: Instructions -->
            <section 
              v-if="isLoadingText || (statementText && statementText !== '<p><br></p>')" 
              class="p-8 bg-slate-50 rounded-[32px] border border-slate-200 shadow-inner"
            >
              <div v-if="isLoadingText" class="text-slate-400 animate-pulse flex items-center gap-2">
                 <div class="w-4 h-4 bg-slate-200 rounded-full"></div>
                 <span class="font-black uppercase text-xs">Loading Test Criteria...</span>
              </div>
              <div v-else class="ql-editor !p-0 text-slate-700" v-html="statementText"></div>
            </section>

            <!-- Section 3: Phonetic Test -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <LanguageIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Phonetic Alphabet Test</h2>
              </div>

              <div class="space-y-6">
                <div class="TestInputGroup">
                  <label for="answerDavid" class="form-label text-slate-400">Word A: <span class="text-slate-900 font-black ml-1">DAVID</span></label>
                  <input type="text" id="answerDavid" v-model="formData.answerDavid" required class="form-input" :class="validationClasses.david" placeholder="Example: Tango, Romeo, Alpha..." />
                </div>
                <div class="TestInputGroup">
                  <label for="answerJohn" class="form-label text-slate-400">Word B: <span class="text-slate-900 font-black ml-1">JOHN</span></label>
                  <input type="text" id="answerJohn" v-model="formData.answerJohn" required class="form-input" :class="validationClasses.john" />
                </div>
                <div class="TestInputGroup">
                  <label for="answerLondon" class="form-label text-slate-400">Word C: <span class="text-slate-900 font-black ml-1">LONDON</span></label>
                  <input type="text" id="answerLondon" v-model="formData.answerLondon" required class="form-input" :class="validationClasses.london" />
                </div>
                <div class="TestInputGroup">
                  <label for="answer976" class="form-label text-slate-400">Numerical D: <span class="text-slate-900 font-black ml-1">976</span></label>
                  <input type="text" id="answer976" v-model="formData.answer976" required class="form-input" :class="validationClasses.num976" placeholder="Example: One, Eight, Zero" />
                </div>
                <div class="TestInputGroup">
                  <label for="answer10" class="form-label text-slate-400">Numerical E: <span class="text-slate-900 font-black ml-1">10</span></label>
                  <input type="text" id="answer10" v-model="formData.answer10" required class="form-input" :class="validationClasses.num10" />
                </div>
              </div>
            </section>
            
            <!-- Section 4: Signature Verification -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <PencilSquareIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">3. Verification Signature</h2>
              </div>

              <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                  <div class="max-w-md mx-auto">
                    <label class="form-label text-slate-400 text-center mb-6 uppercase tracking-widest">Official Record Signature</label>
                    <div class="bg-white p-8 rounded-[32px] border-2 border-dashed border-slate-200 shadow-inner flex flex-col items-center">
                        <div v-if="userProfile.signatureUrl || userProfile.signatureDataUrl" class="animate-fadeIn">
                            <img :src="userProfile.signatureUrl || userProfile.signatureDataUrl" alt="Signature Preview" class="max-h-24 object-contain" />
                            <div class="mt-6 flex items-center justify-center gap-2 bg-[#EBFDF8] px-4 py-2 rounded-full border border-[#20FCF1]/20">
                              <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
                              <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">Identity Verified</span>
                            </div>
                        </div>
                        <div v-else class="text-center space-y-4">
                            <div class="bg-red-50 p-4 rounded-xl text-red-600 text-xs font-black uppercase tracking-tight border border-red-100">
                                Signature Missing from Profile
                            </div>
                            <button @click="$router.push({ name: 'yourDetails' })" type="button" class="px-6 py-2 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest">
                                Update Details
                            </button>
                        </div>
                    </div>
                  </div>
              </div>
            </section>

            <!-- Submit Button Area -->
            <div class="pt-10">
              <button 
                type="submit" 
                :disabled="isSubmitting || !isTestPassed" 
                class="btn-premium disabled:bg-slate-200 disabled:text-slate-400"
              >
                <span v-if="isSubmitting" class="flex items-center gap-3">
                    <svg class="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Validating & Submitting...
                </span>
                <span v-else>
                    {{ isTestPassed ? 'Submit Literacy Test Result' : 'All Answers Must Be Correct to Proceed' }}
                </span>
              </button>
            </div>
          </form>

          <p v-if="successMessage" class="mt-8 p-6 bg-mint-50 border border-brand-accent rounded-[32px] text-center shadow-lg animate-bounce font-black text-brand-primary uppercase tracking-widest">{{ successMessage }}</p>
          <p v-if="errorMessage" class="mt-8 p-6 bg-red-50 border border-red-200 rounded-[32px] text-center font-bold text-red-700">{{ errorMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useRouter } from 'vue-router';
import FormHeader from '@/components/FormHeader.vue';

// Premium Icons
import { 
  IdentificationIcon, 
  LanguageIcon, 
  PencilSquareIcon, 
  CheckBadgeIcon 
} from '@heroicons/vue/24/outline';

const router = useRouter();
const { user } = useAuth();
const functions = getFunctions(firebaseApp, 'europe-west2');

const userProfile = reactive({ name: '', email: '', sentinelNumber: '', signatureUrl: '', signatureDataUrl: '' });
const formData = reactive({
  answerDavid: '',
  answerJohn: '',
  answerLondon: '',
  answer976: '',
  answer10: '',
});

const statementText = ref('');
const isLoadingText = ref(true);
const isSubmitting = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

const normalizeAnswer = (answer) => answer.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();

const correctAnswers = {
    david: 'delta alpha victor india delta',
    john: 'juliet oscar hotel november',
    london: 'lima oscar november delta oscar november',
    num976: 'nine seven six',
    num10: 'one zero',
};

const validationClasses = computed(() => {
    const getValidationClass = (answer, correctAnswer) => {
        if (!answer) return 'border-slate-200';
        const isCorrect = normalizeAnswer(answer) === correctAnswer;
        if (isCorrect) {
            return 'bg-mint-50 border-[#20FCF1] text-[#0A1630] focus:ring-[#20FCF1] focus:border-[#20FCF1]';
        } else {
            return 'bg-red-50 border-red-300 text-red-800 focus:ring-red-500 focus:border-red-500 shadow-inner shadow-red-100/50';
        }
    };
    return {
        david: getValidationClass(formData.answerDavid, correctAnswers.david),
        john: getValidationClass(formData.answerJohn, correctAnswers.john),
        london: getValidationClass(formData.answerLondon, correctAnswers.london),
        num976: getValidationClass(formData.answer976, correctAnswers.num976),
        num10: getValidationClass(formData.answer10, correctAnswers.num10),
    };
});

const isTestPassed = computed(() => {
    return normalizeAnswer(formData.answerDavid) === correctAnswers.david &&
           normalizeAnswer(formData.answerJohn) === correctAnswers.john &&
           normalizeAnswer(formData.answerLondon) === correctAnswers.london &&
           normalizeAnswer(formData.answer976) === correctAnswers.num976 &&
           normalizeAnswer(formData.answer10) === correctAnswers.num10;
});

const loadPrerequisites = async () => {
  if (!user.value) {
    errorMessage.value = "Authentication not found. Please log in again.";
    isLoadingText.value = false;
    return;
  }
  
  try {
    isLoadingText.value = true;
    const userProfileRef = doc(db, 'userProfiles', user.value.uid);
    const idTokenResult = await user.value.getIdTokenResult();
    const clientId = idTokenResult.claims.clientId;

    if (!clientId) throw new Error("Client association not found.");

    const clientDocRef = doc(db, 'clients', clientId);

    const [userProfileSnap, clientDocSnap] = await Promise.all([
      getDoc(userProfileRef),
      getDoc(clientDocRef)
    ]);

    if (userProfileSnap.exists()) {
      const profile = userProfileSnap.data();
      userProfile.name = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
      userProfile.email = profile.email || user.value.email;
      userProfile.sentinelNumber = profile.sentinelNumber || 'N/A';
      userProfile.signatureUrl = profile.signatureUrl || '';
      userProfile.signatureDataUrl = profile.signatureDataUrl || '';
    } else {
      throw new Error("Please complete the 'Your Details' form first.");
    }

    if (clientDocSnap.exists()) {
      const clientData = clientDocSnap.data();
      statementText.value = clientData.onboardingFormTexts?.literacyTest || '';
    }

  } catch (error) {
    errorMessage.value = `Error loading prerequisites: ${error.message}`;
  } finally {
    isLoadingText.value = false;
  }
};

onMounted(loadPrerequisites);

const submitForm = async () => {
  if (!isTestPassed.value) {
    errorMessage.value = "Please ensure all answers are correct before submitting.";
    return;
  }
  isSubmitting.value = true;
  successMessage.value = '';
  errorMessage.value = '';

  const submissionData = {
    ...formData,
    name: userProfile.name,
    email: userProfile.email,
    sentinelNumber: userProfile.sentinelNumber,
    signatureUrl: userProfile.signatureUrl,
    signatureDataUrl: userProfile.signatureDataUrl,
    status: 'completed',
    statementText: statementText.value
  };

  try {
    const submitFormFunction = httpsCallable(functions, 'submitGenericForm');
    const result = await submitFormFunction({
      formIdentifier: 'literacyTest',
      targetCollection: 'onboardingFormSubmissions',
      submissionData: submissionData
    });
    if (result.data.success) {
      successMessage.value = `Literacy Test submitted successfully! Reference: ${result.data.referenceNumber}`;
      setTimeout(() => router.push('/onboarding-forms'), 2000);
    } else {
      throw new Error(result.data.error || 'The form submission failed.');
    }
  } catch (error) {
    errorMessage.value = `Failed to submit form. ${error.message}`;
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.form-input { 
  @apply block w-full px-4 py-3 bg-white border rounded-xl text-slate-700 shadow-sm transition-all sm:text-sm; 
}
.form-label { 
  @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2; 
}
.btn-premium { 
  @apply w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all flex justify-center items-center; 
}

.TestInputGroup {
  @apply bg-slate-50 p-6 rounded-3xl border border-slate-100 transition-all hover:bg-white hover:border-[#20FCF1]/20 hover:shadow-sm;
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Quill Styling */
:deep(.ql-editor) {
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.8;
  color: #334155;
}
:deep(.ql-editor p) {
  margin-bottom: 1.25rem;
}
:deep(.ql-editor h2) {
  @apply text-xl font-black uppercase tracking-tight text-slate-900 mt-8 mb-4;
}
</style>